import { useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyField,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

// ─── Slot Group Definitions ───────────────────────────────────────────────────

const amenitiesLinkDef: SlotGroupDefinition = {
  label: 'Amenities CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Field generator for a card's label. The label is a FREE (unmapped) field so
// the value isn't forced to be the amenity's `title`: the generator may instead
// distill the amenity's `description` into a short headline when that reads as a
// stronger, more descriptive label. `defaultField` keeps the amenity title as
// the non-AI/preview fallback.
const cardLabelGenerator = {
  prompt:
    'Write this card’s short label for the selected amenity. Prefer the amenity’s title, but when its description reads as a stronger, more descriptive headline, distill that description into a concise title.',
  context: 'Overlay heading on an amenity card — a short phrase, not a sentence.',
  length: 'a few words',
} as const;

// Group generator: select a distinct amenity per card and guarantee a real
// photo. Amenities frequently have no image of their own, so the generator may
// pull one from `photo_gallery`; the runtime also falls back to a matching (or
// first available) gallery photo so a card is never imageless.
const cardImagePrompt =
  'Pick one of the property’s most appealing amenities and pair it with a real photo pulled from photo_gallery. Every card must have an image sourced from photo_gallery — never leave a card imageless. Across the three cards choose a different gallery photo each so no available gallery image is ignored; only fall back to the amenity’s own image when photo_gallery has no fitting photo left.';

// Three amenity cards — each bound to an `amenities` collection item (index
// 0-2). The image/alt autofill from the amenity (with a gallery fallback); the
// label is generator-authored from the amenity's title OR description. All
// fields stay editable.
const amenityCard1Def: SlotGroupDefinition = {
  label: 'Amenity Card 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[0].amenity_image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'amenities[0].amenity_image_alt_text',
      defaultValue: 'Resort-style pool',
    },
    title: {
      label: 'Label',
      type: 'text',
      defaultField: 'amenities[0].title',
      defaultValue: 'Resort-Style Pool',
      generator: cardLabelGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 0,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt: cardImagePrompt,
    context:
      'First of three amenity cards in the home page Amenities section — each card must feature a different amenity, ideally covering distinct categories (e.g. pool, fitness, lounge).',
    sourceCollection: 'amenities',
  },
};

const amenityCard2Def: SlotGroupDefinition = {
  label: 'Amenity Card 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[1].amenity_image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'amenities[1].amenity_image_alt_text',
      defaultValue: 'Fitness center',
    },
    title: {
      label: 'Label',
      type: 'text',
      defaultField: 'amenities[1].title',
      defaultValue: 'State-of-the-Art Fitness Center',
      generator: cardLabelGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 1,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt: cardImagePrompt,
    context:
      'Second of three amenity cards in the home page Amenities section — each card must feature a different amenity from the others, ideally covering distinct categories (e.g. pool, fitness, lounge).',
    sourceCollection: 'amenities',
  },
};

const amenityCard3Def: SlotGroupDefinition = {
  label: 'Amenity Card 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'amenities[2].amenity_image' },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'amenities[2].amenity_image_alt_text',
      defaultValue: 'Resident lounge',
    },
    title: {
      label: 'Label',
      type: 'text',
      defaultField: 'amenities[2].title',
      defaultValue: 'Resident Lounge',
      generator: cardLabelGenerator,
    },
  },
  sourceBinding: {
    collection: 'amenities',
    defaultIndex: 2,
    fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text' },
  },
  generator: {
    prompt: cardImagePrompt,
    context:
      'Third of three amenity cards in the home page Amenities section — each card must feature a different amenity from the others, ideally covering distinct categories (e.g. pool, fitness, lounge).',
    sourceCollection: 'amenities',
  },
};

// Inner visual for a manual amenity card (image + gradient + title overlay).
function AmenityCardContent({
  image,
  alt,
  title,
}: {
  image?: string | null;
  alt?: string | null;
  title?: string | null;
}) {
  return (
    <>
      {image ? (
        <VibesitesImage
          url={image}
          alt={alt ?? title ?? ''}
          sizes="(min-width: 768px) 33vw, 100vw"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 w-full h-full bg-[var(--vibe-color-surface)]" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-white/75 via-white/15 to-transparent" />
      {title && (
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p className="font-body text-sm font-semibold text-black tracking-wide">{title}</p>
        </div>
      )}
    </>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function AmenitiesHomeSection() {
  const amenitiesEyebrow = useContentToken('amenitieshomesection.eyebrow');

  const amenitiesLink = useSlotGroup('amenitieshomesection.link', amenitiesLinkDef);
  const amenityCard1 = useSlotGroup('amenitieshomesection.amenity_1', amenityCard1Def);
  const amenityCard2 = useSlotGroup('amenitieshomesection.amenity_2', amenityCard2Def);
  const amenityCard3 = useSlotGroup('amenitieshomesection.amenity_3', amenityCard3Def);

  // Property featured image — used after a card's own image, but before the
  // last-resort gallery default.
  const featuredImageField = usePropertyField('property_featured_image');
  const featuredImage = typeof featuredImageField === 'string' ? featuredImageField : '';

  const galleryImages = useCollectionBinding<GalleryPhoto>('amenitieshomesection.gallery_images', {
    collection: 'photo_gallery',
    defaultLimit: 12,
  });

  // First gallery photo with a usable URL — the last-resort default so a card is
  // never rendered without a photo.
  const firstGalleryPhoto = useMemo(
    () => galleryImages.find((p) => p.image ?? p.original_url) ?? null,
    [galleryImages],
  );

  // Resolve each amenity card's image via the fallback chain:
  //   1. the amenity's own image set on the card (`card.image`)
  //   2. the property's featured image
  //   3. the first gallery photo (the default) — so a card is never imageless
  const resolveAmenityCard = useCallback(
    (card: Record<string, string | null>, fallbackAlt: string) => {
      const title = card.title || '';
      const explicitImage = card.image || '';
      const firstUrl = firstGalleryPhoto?.image || firstGalleryPhoto?.original_url || '';
      const image = explicitImage || featuredImage || firstUrl || '';
      const alt = card.alt || title || fallbackAlt;
      return { image, alt, title };
    },
    [featuredImage, firstGalleryPhoto],
  );

  const amenityCards = useMemo(
    () => [
      resolveAmenityCard(amenityCard1, 'Resort-style pool'),
      resolveAmenityCard(amenityCard2, 'Fitness center'),
      resolveAmenityCard(amenityCard3, 'Resident lounge'),
    ],
    [resolveAmenityCard, amenityCard1, amenityCard2, amenityCard3],
  );

  return (
    <div className="bg-primary py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header row: eyebrow + heading left, button right */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <EditableToken
              path="amenitieshomesection.eyebrow"
              defaultValue="Community and apartment amenities"
              as="p"
              className="font-body text-sm text-[var(--vibe-color-text-on-primary)] mb-3 tracking-wide"
            >
              {amenitiesEyebrow}
            </EditableToken>
            <RichTextContent
              path="amenitieshomesection.heading"
              label="Amenities Heading"
              defaultValue='<p>Elevated Spaces at <span data-type="field-token" data-field="property_name"></span></p>'
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-primary-foreground leading-[1.1]"
            />
          </div>
          <EditableSlotGroup groupPath="amenitieshomesection.link" definition={amenitiesLinkDef}>
            <a
              href={amenitiesLink.url || '/amenities'}
              target={amenitiesLink._target || '_self'}
              className="inline-flex items-center justify-center flex-shrink-0 min-w-[12rem] px-6 py-3 text-sm font-semibold text-primary-foreground border border-primary-foreground/60 rounded-sm hover:bg-primary-foreground hover:text-primary transition-all duration-200"
            >
              {amenitiesLink.label || 'View All Amenities'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* Amenity card grid: stacked on mobile, 3-across from tablet up */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <EditableSlotGroup
            groupPath="amenitieshomesection.amenity_1"
            definition={amenityCard1Def}
            className="relative block rounded-xl overflow-hidden group"
            style={{ aspectRatio: '3/4' }}
          >
            <AmenityCardContent
              image={amenityCards[0].image}
              alt={amenityCards[0].alt}
              title={amenityCards[0].title}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="amenitieshomesection.amenity_2"
            definition={amenityCard2Def}
            className="relative block rounded-xl overflow-hidden group"
            style={{ aspectRatio: '3/4' }}
          >
            <AmenityCardContent
              image={amenityCards[1].image}
              alt={amenityCards[1].alt}
              title={amenityCards[1].title}
            />
          </EditableSlotGroup>
          <EditableSlotGroup
            groupPath="amenitieshomesection.amenity_3"
            definition={amenityCard3Def}
            className="relative block rounded-xl overflow-hidden group"
            style={{ aspectRatio: '3/4' }}
          >
            <AmenityCardContent
              image={amenityCards[2].image}
              alt={amenityCards[2].alt}
              title={amenityCards[2].title}
            />
          </EditableSlotGroup>
        </div>
      </div>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesHomeSection',
  displayName: 'Amenities Home Section',
  tokens: {
    'amenitieshomesection.eyebrow': {
      label: 'Amenities Eyebrow',
      type: 'text',
      defaultValue: 'Community and apartment amenities',
    },
    'amenitieshomesection.heading': {
      label: 'Amenities Heading',
      type: 'richtext',
      defaultValue:
        '<p>Elevated Spaces at <span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'amenitieshomesection.link': {
      label: 'Amenities CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'View All Amenities' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'amenitieshomesection.amenity_1': {
      label: 'Amenity Card 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'amenities[0].amenity_image' },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'amenities[0].amenity_image_alt_text',
          defaultValue: 'Resort-style pool',
        },
        title: {
          label: 'Label',
          type: 'text',
          defaultField: 'amenities[0].title',
          defaultValue: 'Resort-Style Pool',
          generator: {
            prompt:
              'Write this card’s short label for the selected amenity. Prefer the amenity’s title, but when its description reads as a stronger, more descriptive headline, distill that description into a concise title.',
            context: 'Overlay heading on an amenity card — a short phrase, not a sentence.',
            length: 'a few words',
          },
        },
      },
      sourceBinding: {
        collection: 'amenities',
        defaultIndex: 0,
        fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text' },
      },
      generator: {
        prompt:
          'Pick one of the property’s most appealing amenities and pair it with a real photo pulled from photo_gallery. Every card must have an image sourced from photo_gallery — never leave a card imageless. Across the three cards choose a different gallery photo each so no available gallery image is ignored; only fall back to the amenity’s own image when photo_gallery has no fitting photo left.',
        context:
          'First of three amenity cards in the home page Amenities section — each card must feature a different amenity, ideally covering distinct categories (e.g. pool, fitness, lounge).',
        sourceCollection: 'amenities',
      },
    },
    'amenitieshomesection.amenity_2': {
      label: 'Amenity Card 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'amenities[1].amenity_image' },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'amenities[1].amenity_image_alt_text',
          defaultValue: 'Fitness center',
        },
        title: {
          label: 'Label',
          type: 'text',
          defaultField: 'amenities[1].title',
          defaultValue: 'State-of-the-Art Fitness Center',
          generator: {
            prompt:
              'Write this card’s short label for the selected amenity. Prefer the amenity’s title, but when its description reads as a stronger, more descriptive headline, distill that description into a concise title.',
            context: 'Overlay heading on an amenity card — a short phrase, not a sentence.',
            length: 'a few words',
          },
        },
      },
      sourceBinding: {
        collection: 'amenities',
        defaultIndex: 1,
        fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text' },
      },
      generator: {
        prompt:
          'Pick one of the property’s most appealing amenities and pair it with a real photo pulled from photo_gallery. Every card must have an image sourced from photo_gallery — never leave a card imageless. Across the three cards choose a different gallery photo each so no available gallery image is ignored; only fall back to the amenity’s own image when photo_gallery has no fitting photo left.',
        context:
          'Second of three amenity cards in the home page Amenities section — each card must feature a different amenity from the others, ideally covering distinct categories (e.g. pool, fitness, lounge).',
        sourceCollection: 'amenities',
      },
    },
    'amenitieshomesection.amenity_3': {
      label: 'Amenity Card 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'amenities[2].amenity_image' },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'amenities[2].amenity_image_alt_text',
          defaultValue: 'Resident lounge',
        },
        title: {
          label: 'Label',
          type: 'text',
          defaultField: 'amenities[2].title',
          defaultValue: 'Resident Lounge',
          generator: {
            prompt:
              'Write this card’s short label for the selected amenity. Prefer the amenity’s title, but when its description reads as a stronger, more descriptive headline, distill that description into a concise title.',
            context: 'Overlay heading on an amenity card — a short phrase, not a sentence.',
            length: 'a few words',
          },
        },
      },
      sourceBinding: {
        collection: 'amenities',
        defaultIndex: 2,
        fieldMappings: { image: 'amenity_image', alt: 'amenity_image_alt_text' },
      },
      generator: {
        prompt:
          'Pick one of the property’s most appealing amenities and pair it with a real photo pulled from photo_gallery. Every card must have an image sourced from photo_gallery — never leave a card imageless. Across the three cards choose a different gallery photo each so no available gallery image is ignored; only fall back to the amenity’s own image when photo_gallery has no fitting photo left.',
        context:
          'Third of three amenity cards in the home page Amenities section — each card must feature a different amenity from the others, ideally covering distinct categories (e.g. pool, fitness, lounge).',
        sourceCollection: 'amenities',
      },
    },
  },
  collectionBindings: {
    'amenitieshomesection.gallery_images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  },
};

(AmenitiesHomeSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesHomeSection;
